import { useState } from 'react'

/**
 * Hook personalizado para manejar formularios en React.
 *
 * @example
 * const [values, handleInputChange, errors, setErrors, reset] = useImprovedForm({
 *  nombre: 'Ejemplo'
 * });
 * @param {Object} [initialState={}] - Estado inicial del formulario.
 * @return {Object[]} Arreglo con el estado del formulario, funciones para cambiar el estado, y el estado de los errores.
 */
const useImprovedForm = (initialState = {}) => {
  const [values, setValues] = useState(initialState)

  /**
   * Función para restablecer el estado del formulario a su estado inicial.
   * @param {Object} [newFormState=initialState] - Nuevo estado inicial del formulario.
   */
  const reset = (newFormState = initialState) => {
    setValues(newFormState)
  }

  /**
   * Función que maneja el cambio de los valores de los campos del formulario.
   * @param {Object} e - Evento del cambio de los valores de los campos del formulario.
   * @param {Object} e.target - Objeto que representa el elemento que cambio en el formulario.
   * @param {Object} e.target.name - Objeto que representa el nombre del elemento que cambio en el formulario.
   * @param {Object} e.target.dataset - Objeto que representa los atributos del elemento en el formulario.
   * @param {string} e.target.value - Representa el valor del campo que cambio en el formulario.
   * @param {string} e.target.textContent - Valor que representa el texto contenido dentro del elemento que cambio en el formulario (para elementos de texto con inputEditable).
   * @param {Object} e.target.files - Objeto que representa los archivos seleccionados en un campo del tipo "file".
   * @param {Object} e.target.type - Objeto que representa el tipo del elemento que cambio en el formulario.
   * @param {boolean} e.target.checked - Objeto que representa el estado de selección de un campo de tipo "checkbox" o "radio" del formulario.
   */
  const handleInputChange = (e) => {
    const { name, dataset, value, textContent, files, type, checked } = e.target
    const newName = dataset && dataset.id ? dataset.id : name

    // Asignar el valor del elemento del formulario dependiendo de su tipo.
    const newValue =
      textContent && dataset.id
        ? textContent
        : type === 'checkbox'
          ? checked
          : type === 'file' && files[0]
            ? URL.createObjectURL(files[0])
            : value === 'true'
              ? true
              : value === 'false'
                ? false
                : value

    console.log('NEW VALUE', newValue)
    console.log('typeof NEW VALUE', typeof newValue)

    setValues(() => {
      // Verificar si el nombre del campo incluye "."
      if (newName.includes('.')) {
        const fieldNames = newName.split('.')
        let currentValues = { ...values }

        /* Crear objeto anidado */
        for (let i = 0; i < fieldNames.length - 1; i++) {
          currentValues[fieldNames[i]] = currentValues[fieldNames[i]] || {}
          currentValues = currentValues[fieldNames[i]]
        }
        currentValues[fieldNames[fieldNames.length - 1]] = newValue
        return { ...values }
      }

      return {
        ...values,
        [newName]: newValue
      }
    })
  }

  return [values, setValues, handleInputChange, reset]
}

export default useImprovedForm
